import { useState } from "react";
import {
  api,
  type EngagementSummary,
  type PortalAudience,
  type TeamMember,
} from "../lib/api";
import { roleLabel } from "../lib/session";
import { useAction, useResource } from "../lib/useResource";
import {
  Button,
  Empty,
  Failed,
  Field,
  Label,
  SkeletonRows,
  Select,
  TextArea,
} from "../components/primitives";
import { classificationLabel, day } from "../lib/format";

/**
 * Who is on an engagement — and the place people are put on and taken off.
 *
 * `issueAssignment`, `revokeAssignment`, `extendAssignment` and
 * `addClientUser` had all existed in the domain layer since their slices,
 * tested, with no routes and no screen. Only accept and decline were
 * reachable, so a consultant could answer an offer that nobody had any way
 * to make. This is the other end of that.
 *
 * Three relationships in one list, because the question actually asked is
 * "who can see this engagement", not "show me the membership table". They
 * stay distinguishable because what you can do about each differs: a member
 * is named on the engagement, a consultant holds an assignment with an end
 * date, and a client belongs to the organisation.
 */
/**
 * The team itself, without a page around it.
 *
 * Extracted so the engagement overview can show it inline. Clicking an
 * engagement should land on the engagement — what it is called, who is on
 * it, what it is made of — not on Command, which answers a different
 * question: what needs you today.
 */
/**
 * The end of the day someone picked, in their own timezone.
 *
 * `<input type="date">` gives "2026-10-19" and `new Date(...)` reads that
 * as midnight UTC — the start of the day, somewhere else. Access that
 * "ends on the 19th" should include the 19th.
 */
function endOfDay(date: string): string | null {
  if (!date) return null;
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

export function TeamSections({
  engagementId,
  members,
  onChanged,
}: {
  /** Closing out an assignment — the handover and the evaluation — is an
      engagement-scoped act, so the rows below need to know which one. */
  engagementId: string;
  members: TeamMember[];
  onChanged: () => void;
}) {
  return (
        <>
          <Group
            engagementId={engagementId}
            heading="Named on the engagement"
            note="The Leads and Partners responsible for this engagement."
            empty="Nobody is named on this engagement yet"
            rows={members.filter((m) => m.kind === "member")}
            onChanged={onChanged}
          />

          <Group
            engagementId={engagementId}
            heading="Consultants"
            note="Access starts when an assignment is accepted and ends on its expiry date. Revoking access signs the consultant out immediately."
            empty="No consultants yet"
            rows={members.filter((m) => m.kind === "consultant")}
            onChanged={onChanged}
          />

          <Group
            engagementId={engagementId}
            heading="Client side"
            note="Contacts at the client company. Opening this engagement to them does not open any of their other engagements."
            empty="No client contacts yet"
            rows={members.filter((m) => m.kind === "client")}
            onChanged={onChanged}
          />

        </>
  );
}

function Group({
  engagementId,
  heading,
  note,
  empty,
  rows,
  onChanged,
}: {
  engagementId: string;
  heading: string;
  note: string;
  /** Written out, not derived from the heading. `Nobody ${heading}` gave
      "Nobody consultants yet" and "Nobody client side yet" — a template
      that only works while every heading happens to be a plural noun. */
  empty: string;
  rows: TeamMember[];
  onChanged: () => void;
}) {
  return (
    <section className="mt-10">
      <Label as="h2">{heading}</Label>
      <p className="mt-2 max-w-[62ch] text-sm text-secondary">{note}</p>

      {rows.length === 0 && <Empty title={empty} />}

      {rows.map((m) => (
        <MemberRow
          key={`${m.kind}-${m.userId}-${m.assignmentId ?? ""}`}
          engagementId={engagementId}
          member={m}
          onChanged={onChanged}
        />
      ))}
    </section>
  );
}

function MemberRow({
  engagementId,
  member,
  onChanged,
}: {
  engagementId: string;
  member: TeamMember;
  onChanged: () => void;
}) {
  const [extending, setExtending] = useState(false);
  const [newExpiry, setNewExpiry] = useState("");
  const [closing, setClosing] = useState(false);

  const revoke = useAction(async () => {
    await api.post(`/api/assignments/${member.assignmentId}/revoke`, {});
    onChanged();
  });

  const extend = useAction(async () => {
    await api.post(`/api/assignments/${member.assignmentId}/extend`, {
      // End of the chosen day, in the picker's own timezone. `new
      // Date("2026-10-19")` is midnight UTC — the start of the day — so
      // extending "to today" would expire it in the past and hand back
      // exactly the bug being fixed.
      expiresAt: endOfDay(newExpiry),
    });
    setExtending(false);
    onChanged();
  });

  // Three different questions, and treating them as one is why an expired
  // assignment had no Extend button — the single state where extending is
  // the whole point.
  //
  //   live     access is working right now
  //   expired  it lapsed, and extending is exactly the remedy
  //   revoked  deliberately ended; it stays on the record and does not
  //            come back by editing a date
  const live = member.status === "accepted" || member.status === "active";
  const expired = member.status === "expired";
  const revoked = member.status === "revoked";
  // Closing out is for an assignment that has actually ended, either way.
  // confirmOffboarding refuses a live one at the database, so offering it
  // here would be offering a button the server will not honour — the thing
  // this screen has got wrong more than once.
  const ended = expired || revoked;

  return (
    <div className="border-b border-line py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base text-ink">{member.fullName}</p>
          <p className="mt-1 break-all text-sm text-secondary">
            {member.email} · {roleLabel(member.role)}
          </p>
        </div>

        <div className="flex flex-wrap items-baseline gap-4 text-sm">
          {member.scope && (
            <span className="label">
              {member.scope === "engagement"
                ? "Whole engagement"
                : member.scope === "workstream"
                  ? "One workstream"
                  : "Assigned tasks only"}
            </span>
          )}
          <span className={live ? "text-secondary" : "text-danger"}>
            {member.status}
          </span>
          {member.expiresAt && (
            <span className="text-secondary">
              until {day(member.expiresAt)}
            </span>
          )}

          {member.assignmentId && !revoked && (
            <>
              <Button tone="quiet" onClick={() => setExtending((v) => !v)}>
                {expired ? "Give them longer" : "Extend"}
              </Button>
              {/* Revoking an already-expired assignment still means
                  something: it is the difference between access that
                  lapsed and access that was taken away, and the record
                  should be able to say which. */}
              <Button
                tone="danger"
                loading={revoke.pending}
                onClick={() => void revoke.invoke()}
              >
                Revoke
              </Button>
            </>
          )}
        </div>
      </div>

      {extending && (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <Field
            label="New end date"
            type="date"
            value={newExpiry}
            onChange={(e) => setNewExpiry(e.target.value)}
          />
          <Button
            tone="primary"
            disabled={!newExpiry}
            loading={extend.pending}
            onClick={() => void extend.invoke()}
          >
            Extend to this
          </Button>
        </div>
      )}

      {/* Closing out. Two separate acts, neither of which had an interface
          at all until now: confirmOffboarding and recordEvaluation have
          been in the domain layer since Slice 2 with no route in front of
          either, so every assignment this firm has ever ended simply
          stopped — unconfirmed and unassessed.

          Only once it HAS ended, because the handover is a statement about
          work that came back and the evaluation is about work that was
          done. Offering either on a live assignment would ask for a
          judgement nobody is yet in a position to make, and the server
          refuses the first one outright. */}
      {ended && member.assignmentId && (
        <CloseOut
          engagementId={engagementId}
          member={member}
          open={closing}
          onOpen={() => setClosing((v) => !v)}
          onChanged={() => {
            setClosing(false);
            onChanged();
          }}
        />
      )}

      {(revoke.refusal || extend.refusal) && (
        <p className="mt-3 border-l-2 border-danger pl-4 text-sm text-ink">
          {(revoke.refusal ?? extend.refusal)?.message}
        </p>
      )}
    </div>
  );
}

/**
 * The end of an assignment, written down.
 *
 * Neither half is a security boundary and the screen must not imply one.
 * Access lapsed at the expiry date or at revocation, whichever came first,
 * and app.reach stopped counting the assignment the moment it did. What is
 * missing after that is assurance — did anybody check the work came back —
 * and the firm's own view of how it went.
 *
 * The evaluation is Restricted, which means the consultant it is about
 * cannot read it. That is the policy as built and a question the firm has
 * not yet answered; the note below says so plainly, rather than letting
 * somebody write a sentence they assumed would be shared.
 */
function CloseOut({
  engagementId,
  member,
  open,
  onOpen,
  onChanged,
}: {
  engagementId: string;
  member: TeamMember;
  open: boolean;
  onOpen: () => void;
  onChanged: () => void;
}) {
  const [note, setNote] = useState("");
  const [rating, setRating] = useState("4");
  const [timeliness, setTimeliness] = useState("4");
  const [remarks, setRemarks] = useState("");

  const offboard = useAction(async () => {
    await api.post(`/api/assignments/${member.assignmentId}/offboard`, {
      note: note.trim() || undefined,
    });
    onChanged();
  });

  const evaluate = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/evaluations`, {
      consultantId: member.userId,
      rating: Number(rating),
      timeliness: Number(timeliness),
      notes: remarks.trim() || undefined,
    });
    setRemarks("");
    onChanged();
  });

  if (member.offboardedAt && !open) {
    return (
      <p className="mt-3 text-sm text-secondary">
        Handover confirmed {day(member.offboardedAt)} ·{" "}
        <button
          type="button"
          onClick={onOpen}
          className="underline underline-offset-4 hover:text-ink"
        >
          record how it went
        </button>
      </p>
    );
  }

  if (!open) {
    return (
      <div className="mt-3">
        <Button tone="quiet" onClick={onOpen}>
          Close this out
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 grid max-w-2xl gap-5 border-l-2 border-line pl-4">
      {!member.offboardedAt && (
        <div className="grid gap-3">
          <TextArea
            label="Handover"
            hint="What came back, and anything outstanding. It confirms that a person checked; it does not change anyone's access, which ended already."
            rows={2}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div>
            <Button
              tone="secondary"
              loading={offboard.pending}
              onClick={() => void offboard.invoke()}
            >
              Confirm the handover
            </Button>
          </div>
          {offboard.refusal && (
            <p className="border-l-2 border-danger pl-4 text-sm text-ink">
              {offboard.refusal.message}
            </p>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <p className="text-sm text-secondary sm:col-span-2">
          How it went. This is the firm's private record — classified
          Restricted, so {member.fullName.split(" ")[0]} cannot read it.
        </p>
        <Select
          label="Quality of the work"
          value={rating}
          onChange={(e) => setRating(e.target.value)}
        >
          {SCORES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.value} — {s.label}
            </option>
          ))}
        </Select>
        <Select
          label="Timeliness"
          value={timeliness}
          onChange={(e) => setTimeliness(e.target.value)}
        >
          {SCORES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.value} — {s.label}
            </option>
          ))}
        </Select>
        <div className="sm:col-span-2">
          <TextArea
            label="Notes"
            hint="Optional. Written for whoever staffs the next engagement."
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <Button
            tone="primary"
            loading={evaluate.pending}
            onClick={() => void evaluate.invoke()}
          >
            Record it
          </Button>
        </div>
        {evaluate.refusal && (
          <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
            {evaluate.refusal.message}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Five points, each with a word against it.
 *
 * A bare 1 to 5 means whatever the person filling it in thinks it means,
 * and an evaluation two Leads would score differently for the same work is
 * not a record of anything.
 */
const SCORES = [
  { value: "1", label: "Fell short" },
  { value: "2", label: "Needed chasing" },
  { value: "3", label: "Did the job" },
  { value: "4", label: "Strong" },
  { value: "5", label: "Would build a team around them" },
] as const;

/**
 * Giving the client access — the two-step, made visible.
 *
 * The routes for both steps have existed since Slice 3 and **nothing in the
 * interface called either of them**, so there was no way to let a client in
 * at all. The user asked what the steps were, which is the question you ask
 * when a screen has not told you.
 *
 * The two steps are deliberate and the database enforces the order:
 * `app.activate_portal` refuses with a check_violation when
 * `portal_ready_at` is null, "so a half-built workspace cannot be handed to a
 * client by one careless call". This screen shows that rather than hiding it
 * behind a single button that sometimes fails.
 *
 * One ordering consequence worth stating, because it is invisible otherwise:
 * activation notifies the client people who exist **at that moment**. Someone
 * added afterwards still reaches the engagement — activation publishes the
 * engagement record and `app.visible()` covers them from then on — but they
 * get no "your workspace is ready" notification. Add your client contacts
 * before you activate.
 */
export function PortalSteps({
  engagement,
  clientCount,
  onDone,
}: {
  engagement: EngagementSummary;
  clientCount: number;
  onDone: () => void;
}) {
  const ready = Boolean(engagement.portalReadyAt);
  const live = Boolean(engagement.portalActivatedAt);
  // client_visible is the bottom of the classification enum; anything
  // above it is out of a client's reach whatever is published.
  const visibleToClient = engagement.classification === "client_visible";

  const openUp = useAction(async () => {
    await api.post(`/api/engagements/${engagement.id}/classification`, {
      classification: "client_visible",
    });
    onDone();
  });

  const markReady = useAction(async () => {
    await api.post(`/api/engagements/${engagement.id}/ready`, {});
    onDone();
  });

  const activate = useAction(async () => {
    await api.post(`/api/engagements/${engagement.id}/activate-portal`, {});
    onDone();
  });

  /**
   * Closing it again. Opening a client's workspace should look as reversible
   * as it is — a Lead who opens the wrong engagement needs to shut it
   * themselves, in one action.
   *
   * It clears portal_activated_at, which is the single thing app.reach
   * checks for a client, so access stops at once including in sessions
   * already open. Publications are untouched: what was published stays
   * published and reappears if it is reopened, because withdrawal is its own
   * written act and must not happen as a side effect of closing a door.
   */
  const deactivate = useAction(async () => {
    await api.post(`/api/engagements/${engagement.id}/deactivate-portal`, {});
    onDone();
  });

  return (
    <section className="mt-12 border-t border-line pt-7">
      <Label as="h2">Open this engagement to the client</Label>

      <p className="mt-2 max-w-[62ch] text-sm text-secondary">
        {live
          ? "This engagement is open. Registered client contacts can sign in and see the items released to them. Their other engagements remain closed unless you open them separately."
          : "Opening the client portal gives registered contacts access to this engagement only. Complete the steps below before opening it."}
      </p>

      {/* Once it is open, the checklist has nothing left to say. Three
          ticks and a destructive button is a lot of screen to spend on
          "done", and it answers a question nobody is asking any more. The
          question after opening is who can see this and what can they
          see, so that is what replaces it. The steps come back if it is
          ever closed. */}
      {live && <OpenPortal engagementId={engagement.id} />}

      {!live && (
      <ol className="mt-6 flex flex-col gap-5">
        {/* First, because the other three cannot work without it.
            `app.visible` gates a client on reach, publication AND
            classification. A client's ceiling is client_visible and an
            engagement defaults to internal — so before 036 you could mark
            an internal engagement ready, open its portal, notify every
            client contact, and they would be told "your workspace is
            being prepared" forever. Every step reported success. */}
        <Step
          n={0}
          done={visibleToClient}
          title="Make it visible to the client"
          note={
            visibleToClient
              ? "The engagement is marked Client visible."
              : `This engagement is ${classificationLabel(engagement.classification).toLowerCase()}. Change it to Client visible before opening the portal.`
          }
          action={
            !visibleToClient && (
              <Button
                tone="secondary"
                loading={openUp.pending}
                onClick={() => void openUp.invoke()}
              >
                Make it client visible
              </Button>
            )
          }
          refusal={openUp.refusal?.message}
        />

        <Step
          n={1}
          done={clientCount > 0}
          title="Register the client's people"
          note={
            clientCount > 0
              ? `${clientCount} client contact${clientCount === 1 ? "" : "s"} registered. Add anyone else who should receive the opening notification.`
              : "No client contacts are registered yet. Add at least one before opening the portal."
          }
        />

        <Step
          n={2}
          done={ready}
          title="Prepare the workspace"
          note={
            ready
              ? `Marked ready ${day(engagement.portalReadyAt as string)}. The client still cannot see it until you open the portal.`
              : "Confirm that the engagement is ready for the client. This does not open the portal."
          }
          action={
            !ready && (
              <Button
                tone="secondary"
                loading={markReady.pending}
                onClick={() => void markReady.invoke()}
              >
                Mark it ready
              </Button>
            )
          }
          refusal={markReady.refusal?.message}
        />

        <Step
          n={3}
          done={live}
          title="Open it"
          note={
            live
              ? `Opened ${day(engagement.portalActivatedAt as string)}. Closing it stops access at once; anything already released returns if you reopen.`
              : !visibleToClient
                ? "Change the engagement to Client visible before opening it."
                : ready
                ? "Open this engagement to its registered client contacts and notify them. Files and deliverables still need to be released separately."
                : "Mark the engagement ready before opening it."
          }
          action={
            live ? (
              <Button
                tone="danger"
                loading={deactivate.pending}
                onClick={() => void deactivate.invoke()}
              >
                Close it to the client
              </Button>
            ) : (
              <Button
                tone="primary"
                // Both conditions, because the database refuses on
                // either — and an offered button that is always refused
                // is the thing this whole screen keeps getting wrong.
                disabled={!ready || !visibleToClient}
                loading={activate.pending}
                onClick={() => void activate.invoke()}
              >
                Open this engagement
              </Button>
            )
          }
          refusal={(activate.refusal ?? deactivate.refusal)?.message}
        />
      </ol>
      )}

      {live && (
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <Button
            tone="danger"
            loading={deactivate.pending}
            onClick={() => void deactivate.invoke()}
          >
            Close it to the client
          </Button>
          <span className="max-w-[48ch] text-sm text-secondary">
            Access stops immediately. Previously released items will appear
            again if you reopen the portal.
          </span>
        </div>
      )}

      {live && deactivate.refusal && (
        <p className="mt-4 border-l-2 border-danger pl-4 text-sm text-ink">
          {deactivate.refusal.message}
        </p>
      )}
    </section>
  );
}

/**
 * The portal, once it is open.
 *
 * Named people rather than a count, because "2 registered" does not tell
 * you whether the right two. And whether each has ever signed in, because
 * a client who was invited and never came is the single most useful thing
 * this panel can surface — it looks identical to a working portal from
 * every other angle.
 */
function OpenPortal({ engagementId }: { engagementId: string }) {
  const audience = useResource<PortalAudience>(
    () => api.get<PortalAudience>(`/api/engagements/${engagementId}/portal-audience`),
    [engagementId],
  );

  if (audience.resource.state === "loading") return <SkeletonRows rows={3} />;
  if (audience.resource.state === "failed") {
    return (
      <Failed
        message={audience.resource.message}
        correlationId={audience.resource.correlationId}
      />
    );
  }
  if (audience.resource.state !== "ready") return null;

  const { people, published } = audience.resource.data;
  const nobodyHasBeen = people.length > 0 && people.every((p) => !p.lastSignedInAt);
  const total = published.deliverables + published.documents;

  return (
    <div className="mt-6">
      <div className="flex flex-wrap gap-x-10 gap-y-4 border-y border-line py-5">
        <div>
          <p className="label">Can sign in</p>
          <p data-numeric className="mt-1 text-xl text-ink">{people.length}</p>
        </div>
        <div>
          <p className="label">Deliverables released</p>
          <p data-numeric className="mt-1 text-xl text-ink">
            {published.deliverables}
          </p>
        </div>
        <div>
          <p className="label">Documents released</p>
          <p data-numeric className="mt-1 text-xl text-ink">
            {published.documents}
          </p>
        </div>
      </div>

      {/* The two states worth calling out, because both look exactly like
          a healthy portal from the outside. */}
      {total === 0 && (
        <p className="mt-4 max-w-[62ch] text-sm text-ink">
          The portal is open, but nothing has been released to the client yet.
          Release each deliverable from Work and each document from Files.
        </p>
      )}
      {nobodyHasBeen && (
        <p className="mt-4 max-w-[62ch] text-sm text-ink">
          Nobody has signed in yet. They were notified when it opened;
          anyone registered since was not, and will need telling.
        </p>
      )}

      {/* The firm's half of the client's Action Centre. That section has
          always rendered on the portal and nothing could ever put
          anything in it: requestFromClient existed from Slice 3 and the
          only mention of it in the HTTP layer was inside a comment about
          a different route. A portal section that is structurally empty
          is worse than one that is absent. */}
      <AskTheClient engagementId={engagementId} />

      {people.length === 0 ? (
        <Empty title="Nobody at the client can sign in">
          The portal is open to an audience of nobody. Register their people
          above — opening only notified whoever was on file at the time, so
          anyone added since needs telling directly.
        </Empty>
      ) : (
        <ul className="mt-4">
          {people.map((p) => (
            <li
              key={p.userId}
              className="flex flex-wrap items-baseline justify-between gap-3 border-b border-line py-2.5"
            >
              <div className="min-w-0">
                <p className="text-base text-ink">{p.fullName}</p>
                <p className="mt-0.5 break-all text-sm text-secondary">{p.email}</p>
              </div>
              <span className={p.lastSignedInAt ? "text-sm text-secondary" : "text-sm text-danger"}>
                {p.lastSignedInAt
                  ? `last signed in ${day(p.lastSignedInAt)}`
                  : "never signed in"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Step({
  n,
  done,
  title,
  note,
  action,
  refusal,
}: {
  n: number;
  done: boolean;
  title: string;
  note: string;
  action?: React.ReactNode;
  refusal?: string;
}) {
  return (
    <li className="flex gap-4">
      <span
        aria-hidden
        className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center border text-xs ${
          done
            ? "border-ink bg-ink text-canvas"
            : "border-line-strong text-secondary"
        }`}
      >
        {done ? "✓" : n}
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-base text-ink">{title}</p>
        <p className="mt-1 max-w-[58ch] text-sm text-secondary">{note}</p>
        {refusal && (
          <p className="mt-2 border-l-2 border-danger pl-3 text-sm text-ink">
            {refusal}
          </p>
        )}
        {action && <div className="mt-3">{action}</div>}
      </div>
    </li>
  );
}

/**
 * Asking the client for something.
 *
 * Published to them as it is created, in one transaction — the domain
 * function says why: an action the client cannot see is not a request,
 * it is a note to self.
 */
function AskTheClient({ engagementId }: { engagementId: string }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("upload");
  const [dueOn, setDueOn] = useState("");

  const ask = useAction(async () => {
    await api.post(`/api/engagements/${engagementId}/client-requests`, {
      title: title.trim(),
      kind,
      dueOn: dueOn || undefined,
    });
    setTitle("");
    setDueOn("");
    setOpen(false);
  });

  if (!open) {
    return (
      <div className="mt-5">
        <Button tone="quiet" onClick={() => setOpen(true)}>
          Ask the client for something
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-5 grid max-w-2xl gap-4 border-l-2 border-line pl-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Field
          label="What you need"
          hint="It appears in their Action Centre straight away, so write it as you would say it to them."
          maxLength={300}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <Select label="What kind" value={kind} onChange={(e) => setKind(e.target.value)}>
        <option value="upload">Something to send us</option>
        <option value="approval">Something to approve</option>
        <option value="question">A question to answer</option>
        <option value="confirmation">Something to confirm</option>
      </Select>
      <Field
        label="Needed by"
        type="date"
        hint="Optional."
        value={dueOn}
        onChange={(e) => setDueOn(e.target.value)}
      />
      <div className="flex gap-3 sm:col-span-2">
        <Button
          tone="primary"
          loading={ask.pending}
          disabled={!title.trim()}
          onClick={() => void ask.invoke()}
        >
          Ask them
        </Button>
        <Button tone="quiet" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      {ask.refusal && (
        <p className="border-l-2 border-danger pl-4 text-sm text-ink sm:col-span-2">
          {ask.refusal.message}
        </p>
      )}
    </div>
  );
}
